import React from 'react'
import { makeStyles, Popper, ClickAwayListener } from '@material-ui/core'
import { Paper } from '../../'
import type { Theme } from '@material-ui/core/styles'
import type { StyleClasses } from '../../styles'
import CheckIcon from '@material-ui/icons/Check'

export interface SwatchPickerProps {
  classes?: StyleClasses<typeof useStyles>
  /** For "popper" mode */
  open?: boolean
  anchorEl?: null | HTMLElement
  /** Common props */
  colors: string[]
  onClick: (color: string) => void
  onClose?: () => void
  currentColor?: string
  /** Variant: popper or inline */
  variant?: 'popper' | 'inline'
  /** Size of swatch circle (px) */
  swatchSize?: number
  /** Layout for swatches */
  layout?: 'wrap' | 'row' | 'column'
}

const useStyles = makeStyles<Theme, SwatchPickerProps>(
  (theme) => ({
    root: {
      display: 'flex',
      padding: theme.spacing(1),
      maxWidth: 258,
      backgroundColor: theme.palette.background.paper,
      borderRadius: theme.shape.borderRadius,
      margin: theme.spacing(1)
    },
    wrap: {
      flexWrap: 'wrap'
    },
    row: {
      flexDirection: 'row',
      flexWrap: 'wrap'
    },
    column: {
      flexDirection: 'column',
      flexWrap: 'wrap'
    },
    popper: {
      zIndex: theme.zIndex.modal
    },
    swatch: {
      borderRadius: '50%',
      margin: theme.spacing(0.5),
      border: '2px solid transparent',
      transition: 'border-color 0.2s',
      '&:hover': {
        borderColor: '#ffffff',
        cursor: 'pointer'
      },
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& svg': {
        color: '#ffffff'
      }
    },
    selected: {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}`
    }
  }),
  { name: 'SwatchPicker' }
)

function SwatchPicker(props: SwatchPickerProps) {
  const {
    open,
    anchorEl,
    colors,
    onClick,
    onClose,
    currentColor,
    variant = 'popper',
    swatchSize = 32,
    layout = 'wrap'
  } = props
  const classes = useStyles(props)

  const handleColorClick = (color: string) => {
    if (color === currentColor) {
      onClose?.()
      return
    }
    onClick(color)
  }

  const swatches = (
    <Paper className={`${classes.root} ${classes[layout]}`}>
      {colors.map((color) => (
        <div
          key={color}
          className={classes.swatch}
          style={{
            backgroundColor: color,
            width: swatchSize,
            height: swatchSize
          }}
          onClick={(ev) => {
            ev.stopPropagation()
            handleColorClick(color)
          }}
        >
          {color === currentColor && <CheckIcon fontSize="small" />}
        </div>
      ))}
    </Paper>
  )

  if (variant === 'inline') {
    return swatches
  }

  return (
    <ClickAwayListener onClickAway={onClose ?? (() => {})}>
      <Popper open={!!open} anchorEl={anchorEl} placement="left" className={classes.popper}>
        {swatches}
      </Popper>
    </ClickAwayListener>
  )
}

export default SwatchPicker
